import { FC } from 'react';
import { css } from '@emotion/react';
import { spacing, colors } from '../../../styles';

interface ParsedToolOutput {
  type?: string;
  status?: string;
  message?: string;
  [key: string]: any;
}

interface ToolRendererProps {
  toolName: string;
  output: ParsedToolOutput;
  isLoading?: boolean;
}

export const BacktestRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Running backtest...
      </div>
    );
  }

  const results = output.results || output.backtest || output;
  if (results && typeof results === 'object') {
    const totalReturn = results.total_return || results.total_return_pct;
    const sharpeRatio = results.sharpe_ratio || results.sharpe;
    const maxDrawdown = results.max_drawdown || results.max_drawdown_pct;

    return (
      <div
        css={css`
          margin: ${spacing.centi} 0 0 0;
        `}
      >
        <div
          css={css`
            font-size: 12px;
            color: ${colors.silver};
            margin-bottom: ${spacing.centi};
          `}
        >
          Backtest results
        </div>
        {totalReturn && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
              font-size: 14px;
              line-height: 1.4;
            `}
          >
            <span
              css={css`
                color: ${colors.silver};
              `}
            >
              Total Return
            </span>
            <span
              css={css`
                color: ${totalReturn > 0 ? colors.leaf : colors.berry};
                font-weight: 500;
              `}
            >
              {typeof totalReturn === 'number' && !isNaN(totalReturn) ? (totalReturn * 100).toFixed(1) : '0.0'}%
            </span>
          </div>
        )}
        {sharpeRatio && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
              font-size: 14px;
              line-height: 1.4;
            `}
          >
            <span
              css={css`
                color: ${colors.silver};
              `}
            >
              Sharpe Ratio
            </span>
            <span
              css={css`
                color: ${colors.liquorice};

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              {sharpeRatio.toFixed(2)}
            </span>
          </div>
        )}
        {maxDrawdown && (
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              margin-bottom: ${spacing.centi};
              font-size: 14px;
              line-height: 1.4;
            `}
          >
            <span
              css={css`
                color: ${colors.silver};
              `}
            >
              Max Drawdown
            </span>
            <span
              css={css`
                color: ${colors.berry};
                font-weight: 500;
              `}
            >
              {typeof maxDrawdown === 'number' && !isNaN(maxDrawdown)
                ? (Math.abs(maxDrawdown) * 100).toFixed(1)
                : '0.0'}
              %
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      css={css`
        font-size: 14px;
        color: ${colors.silver};
      `}
    >
      Backtest completed
    </div>
  );
};
